import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getInstallations } from '../api/portal';
import { InstallationWithApiKey } from '../types';

const SELECTED_INSTALLATION_KEY = 'portal_selected_installation_id';

interface InstallationContextType {
  installations: InstallationWithApiKey[];
  selectedInstallation: InstallationWithApiKey | null;
  selectInstallation: (id: string) => void;
  refreshInstallations: () => Promise<void>;
  loading: boolean;
}

const InstallationContext = createContext<InstallationContextType | undefined>(undefined);

export const useInstallation = () => {
  const context = useContext(InstallationContext);
  if (!context) {
    throw new Error('useInstallation must be used within an InstallationProvider');
  }
  return context;
};

interface InstallationProviderProps {
  children: React.ReactNode;
}

export const InstallationProvider: React.FC<InstallationProviderProps> = ({ children }) => {
  const [installations, setInstallations] = useState<InstallationWithApiKey[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(SELECTED_INSTALLATION_KEY);
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  const loadInstallations = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getInstallations();
      setInstallations(result);

      // If no selection or selection no longer valid, auto-select first
      if (result.length > 0) {
        const currentValid = selectedId && result.some((inst) => inst.id === selectedId);
        if (!currentValid) {
          const firstId = result[0].id;
          setSelectedId(firstId);
          try {
            localStorage.setItem(SELECTED_INSTALLATION_KEY, firstId);
          } catch {
            // localStorage unavailable
          }
        }
      } else {
        setSelectedId(null);
        try {
          localStorage.removeItem(SELECTED_INSTALLATION_KEY);
        } catch {
          // localStorage unavailable
        }
      }
    } catch (error) {
      console.error('Failed to load installations:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => {
    loadInstallations();
    // Only run on mount — selectedId dependency handled internally
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectInstallation = useCallback((id: string) => {
    setSelectedId(id);
    try {
      localStorage.setItem(SELECTED_INSTALLATION_KEY, id);
    } catch {
      // localStorage unavailable
    }
  }, []);

  const refreshInstallations = useCallback(async () => {
    await loadInstallations();
  }, [loadInstallations]);

  const selectedInstallation =
    installations.find((inst) => inst.id === selectedId) || installations[0] || null;

  const value: InstallationContextType = {
    installations,
    selectedInstallation,
    selectInstallation,
    refreshInstallations,
    loading,
  };

  return <InstallationContext.Provider value={value}>{children}</InstallationContext.Provider>;
};

export default InstallationContext;
